import { cn } from "@/lib/utils";
import { RiCloseCircleFill, RiCloseLargeLine } from "@remixicon/react";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../ui/carousel";

const FilePreview = ({
  files,
  urls,
  className,
  smallPreviewItemClassName,
  onRemove,
}: {
  files?: File[];
  urls?: string[];
  className?: string;
  smallPreviewItemClassName?: string;
  onRemove?: (file: File | string) => void;
}) => {
  return (
    <div
      data-testid="file-preview"
      className={cn("flex flex-row gap-2", className)}
    >
      {urls ? (
        <>
          {urls.map((url, index) => {
            return url.includes(".pdf") ? (
              <div key={index} className="file-item">
                <SmallPdfPreview
                  url={url}
                  urls={urls}
                  smallPreviewItemClassName={smallPreviewItemClassName}
                  onRemove={onRemove}
                />
              </div>
            ) : (
              <div key={index} className="file-item">
                <SmallImagePreview
                  url={url}
                  urls={urls}
                  smallPreviewItemClassName={smallPreviewItemClassName}
                  onRemove={onRemove}
                />
              </div>
            );
          })}
        </>
      ) : (
        (files ?? []).map((file, index) => (
          <div key={index} className="file-item">
            {file.type.startsWith("image/") ? (
              <SmallImagePreview
                file={file}
                files={files}
                smallPreviewItemClassName={smallPreviewItemClassName}
                onRemove={onRemove}
              />
            ) : file.type === "application/pdf" ? (
              <SmallPdfPreview
                file={file}
                files={files}
                urls={urls}
                smallPreviewItemClassName={smallPreviewItemClassName}
                onRemove={onRemove}
              />
            ) : (
              <span className="text-[15px] text-white">{file.name}</span>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default FilePreview;

const useImageZoom = () => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isZoomed, setIsZoomed] = useState(false);
  const scaleRef = useRef(1);
  const positionRef = useRef({ x: 0, y: 0 });
  const isZoomedRef = useRef(false);
  const initialScale = useRef(1);
  const initialPosition = useRef({ x: 0, y: 0 });
  const lastTouchDistance = useRef<number | null>(null);
  const lastTouchCenter = useRef<{ x: number; y: number } | null>(null);
  const lastSingleTouch = useRef<{ x: number; y: number } | null>(null);
  const lastTapTime = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // Keep refs in sync with state
  useEffect(() => {
    scaleRef.current = scale;
    positionRef.current = position;
    isZoomedRef.current = isZoomed;
  }, [scale, position, isZoomed]);

  // Reset position when scale is 1
  useEffect(() => {
    if (scale === 1 && (position.x !== 0 || position.y !== 0)) {
      setPosition({ x: 0, y: 0 });
      initialPosition.current = { x: 0, y: 0 };
    }
  }, [scale, position]);

  const getDistance = (touch1: Touch, touch2: Touch) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getCenter = (touch1: Touch, touch2: Touch) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  };

  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setIsZoomed(false);
    initialScale.current = 1;
    initialPosition.current = { x: 0, y: 0 };
  };

  const constrainPosition = (
    newScale: number,
    newPosition: { x: number; y: number }
  ) => {
    if (!containerRef.current || !imageRef.current) return newPosition;

    const container = containerRef.current;
    const containerRect = container.getBoundingClientRect();
    const image = imageRef.current;

    // Get natural image dimensions
    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;
    const aspectRatio = naturalWidth / naturalHeight;

    // Calculate displayed dimensions
    let displayedWidth = containerRect.width;
    let displayedHeight = containerRect.height;

    if (displayedWidth / displayedHeight > aspectRatio) {
      displayedHeight = displayedWidth / aspectRatio;
    } else {
      displayedWidth = displayedHeight * aspectRatio;
    }

    const scaledWidth = displayedWidth * newScale;
    const scaledHeight = displayedHeight * newScale;

    const maxX = Math.max(0, (scaledWidth - containerRect.width) / 2);
    const maxY = Math.max(0, (scaledHeight - containerRect.height) / 2);

    return {
      x: Math.max(-maxX, Math.min(maxX, newPosition.x)),
      y: Math.max(-maxY, Math.min(maxY, newPosition.y)),
    };
  };

  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      e.stopPropagation();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      lastTouchDistance.current = getDistance(touch1, touch2);
      lastTouchCenter.current = getCenter(touch1, touch2);
      initialScale.current = scaleRef.current;
      initialPosition.current = { ...positionRef.current };
      lastSingleTouch.current = null;
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      const now = Date.now();
      const timeSinceLastTap = now - lastTapTime.current;

      if (timeSinceLastTap < 300 && timeSinceLastTap > 0) {
        // Double tap
        e.preventDefault();
        e.stopPropagation();
        if (isZoomedRef.current) {
          resetZoom();
        } else {
          const container = containerRef.current;
          if (container) {
            const rect = container.getBoundingClientRect();
            const centerX = touch.clientX - rect.left - rect.width / 2;
            const centerY = touch.clientY - rect.top - rect.height / 2;
            const newScale = 2.5;
            const newPosition = {
              x: -centerX * (newScale - 1),
              y: -centerY * (newScale - 1),
            };
            const constrained = constrainPosition(newScale, newPosition);
            setScale(newScale);
            setPosition(constrained);
            setIsZoomed(true);
            initialScale.current = newScale;
            initialPosition.current = constrained;
          }
        }
      } else if (isZoomedRef.current) {
        e.preventDefault();
        e.stopPropagation();
        lastSingleTouch.current = { x: touch.clientX, y: touch.clientY };
        initialPosition.current = { ...positionRef.current };
      }
      lastTapTime.current = now;
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      e.stopPropagation();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = getDistance(touch1, touch2);
      const center = getCenter(touch1, touch2);

      if (
        lastTouchDistance.current &&
        lastTouchCenter.current &&
        containerRef.current
      ) {
        const scaleChange = distance / lastTouchDistance.current;
        const newScale = Math.max(
          1,
          Math.min(5, initialScale.current * scaleChange)
        );

        const rect = containerRef.current.getBoundingClientRect();
        const centerX = center.x - rect.left - rect.width / 2;
        const centerY = center.y - rect.top - rect.height / 2;

        // Calculate new position based on zoom center
        const newPosition = {
          x: initialPosition.current.x - centerX * (scaleChange - 1),
          y: initialPosition.current.y - centerY * (scaleChange - 1),
        };

        if (newScale <= 1) {
          // Reset to initial state when zoom is 1 or below
          setScale(1);
          setPosition({ x: 0, y: 0 });
          setIsZoomed(false);
          initialScale.current = 1;
          initialPosition.current = { x: 0, y: 0 };
        } else {
          const constrained = constrainPosition(newScale, newPosition);
          setScale(newScale);
          setPosition(constrained);
          setIsZoomed(true);
        }
        lastTouchCenter.current = center;
      }
    } else if (
      e.touches.length === 1 &&
      isZoomedRef.current &&
      lastSingleTouch.current
    ) {
      e.preventDefault();
      e.stopPropagation();
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastSingleTouch.current.x;
      const deltaY = touch.clientY - lastSingleTouch.current.y;

      const newPosition = {
        x: initialPosition.current.x + deltaX,
        y: initialPosition.current.y + deltaY,
      };

      const constrained = constrainPosition(scaleRef.current, newPosition);
      setPosition(constrained);
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (e.touches.length === 0) {
      lastTouchDistance.current = null;
      lastTouchCenter.current = null;
      lastSingleTouch.current = null;
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      lastSingleTouch.current = { x: touch.clientX, y: touch.clientY };
      initialPosition.current = { ...positionRef.current };
    }
  };

  // Add native event listeners with passive: false
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    container.addEventListener("touchend", handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    scale,
    position,
    isZoomed,
    containerRef,
    imageRef,
    resetZoom,
  };
};

const ZoomableImage = ({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className?: string;
}) => {
  const { scale, position, isZoomed, containerRef, imageRef } = useImageZoom();

  return (
    <div
      ref={containerRef}
      className="relative flex h-[80vh] w-[80vw] items-center justify-center overflow-hidden"
      style={{ touchAction: "none" }}
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        className={cn("h-full w-full object-contain", className)}
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: "center center",
          transition: isZoomed ? "none" : "transform 0.2s ease-out",
        }}
        draggable={false}
      />
    </div>
  );
};

const SmallImagePreview = ({
  file,
  url,
  files,
  urls,
  smallPreviewItemClassName,
  onRemove,
}: {
  file?: File;
  files?: File[];
  url?: string;
  urls?: string[];
  smallPreviewItemClassName?: string;
  onRemove?: (file: File | string) => void;
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <>
      <button
        role="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setVisible(true);
        }}
        data-testid={`file-preview-item-image-${file?.name ?? "url"}`}
        className="relative"
      >
        {onRemove && (
          <span
            role="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove?.(url ?? file ?? new File([], ""));
            }}
            className="absolute right-0 top-0 z-10"
          >
            <RiCloseCircleFill
              data-testid="file-preview-item-image-remove-button"
              className="text-red-500"
            />
          </span>
        )}

        <img
          src={url ?? URL.createObjectURL(file ?? new Blob())}
          alt={file?.name ?? ""}
          className={cn(
            "image-preview h-[70px] w-full max-w-[100px] rounded-md object-contain",
            smallPreviewItemClassName
          )}
        />
      </button>

      <OverlayContainer
        file={file}
        files={files}
        url={url}
        urls={urls}
        visible={visible}
        setVisible={setVisible}
      />
    </>
  );
};

const SmallPdfPreview = ({
  file,
  url,
  files,
  urls,
  smallPreviewItemClassName,
  onRemove,
}: {
  file?: File;
  url?: string;
  files?: File[];
  urls?: string[];
  smallPreviewItemClassName?: string;
  onRemove?: (file: File | string) => void;
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <>
      <button
        role="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setVisible(true);
        }}
        data-testid={`file-preview-item-pdf-${file?.name ?? "url"}`}
        className="relative"
      >
        {onRemove && (
          <span
            role="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove?.(url ?? file ?? new File([], ""));
            }}
            className="absolute right-0 top-0"
          >
            <RiCloseCircleFill
              data-testid="file-preview-item-pdf-remove-button"
              className="text-red-500"
            />
          </span>
        )}

        <iframe
          title="PDF Preview"
          src={
            file
              ? URL.createObjectURL(file)
              : url + "#toolbar=0&navpanes=0&scrollbar=0&&zoom=1"
          }
          className={cn(
            "pdf-preview pointer-events-none h-[70px] w-full max-w-[100px] rounded-md bg-white",
            smallPreviewItemClassName
          )}
        />
      </button>

      <OverlayContainer
        file={file}
        files={files}
        url={url}
        urls={urls}
        visible={visible}
        setVisible={setVisible}
      />
    </>
  );
};

const OverlayContainer = ({
  visible,
  setVisible,
  files,
  file: selectedFile,
  urls,
  url: selectedUrl,
}: {
  files?: File[];
  file?: File;
  urls?: string[];
  url?: string;
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  React.useEffect(() => {
    if (selectedFile && files?.indexOf(selectedFile)) {
      api?.scrollTo(files?.indexOf(selectedFile), true);
    }
  }, [api, selectedFile, files]);

  React.useEffect(() => {
    if (selectedUrl && urls?.indexOf(selectedUrl)) {
      api?.scrollTo(urls?.indexOf(selectedUrl), true);
    }
  }, [api, selectedUrl, urls]);

  if (!visible) return null;

  return createPortal(
    <div
      style={{
        pointerEvents: "all",
      }}
      className={cn(
        "fixed left-0 top-0 z-[9999] flex h-screen max-h-screen w-screen items-center justify-center gap-4 overflow-auto bg-black/50 py-10",
        visible ? "flex" : "hidden"
      )}
    >
      <span
        role="button"
        onClick={(e) => {
          e.stopPropagation();
          setVisible(false);
        }}
        className="absolute right-2 top-4 px-2"
      >
        <RiCloseLargeLine className="text-white" />
      </span>

      <Carousel opts={{ loop: true, align: "center" }} setApi={setApi}>
        <CarouselContent className="max-h-[80vh] max-w-[80vw]">
          {files?.length &&
            files?.length > 0 &&
            files.map((file, index) => {
              if (file.type.startsWith("image/")) {
                return (
                  <CarouselItem
                    className="flex items-center justify-center pr-0"
                    key={file.name + index}
                    data-active={current === index + 1}
                    data-testid={`file-preview-item-image-previewed-${file.name}`}
                  >
                    <ZoomableImage
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                    />
                  </CarouselItem>
                );
              } else if (file.type === "application/pdf") {
                return (
                  <CarouselItem
                    className="flex items-center justify-center pr-0"
                    key={file.name + index}
                    data-active={current === index + 1}
                    data-testid={`file-preview-item-pdf-previewed-${file.name}`}
                  >
                    <div className="flex h-[80vh] w-[80vw] items-center justify-center">
                      <iframe
                        className="h-full w-full bg-white object-contain"
                        src={URL.createObjectURL(file)}
                        title="PDF Preview"
                      />
                    </div>
                  </CarouselItem>
                );
              }
            })}

          {urls?.length &&
            urls?.length > 0 &&
            urls.map((url, index) => {
              if (url.includes(".pdf")) {
                return (
                  <CarouselItem
                    className="flex items-center justify-center pr-0"
                    key={url + index}
                    data-active={current === index + 1}
                    data-testid={`file-preview-item-pdf-previewed-url`}
                  >
                    <div className="flex h-[80vh] w-[80vw] items-center justify-center">
                      <iframe
                        className="h-full w-full bg-white object-contain"
                        src={url}
                        title="PDF Preview"
                      />
                    </div>
                  </CarouselItem>
                );
              } else {
                return (
                  <CarouselItem
                    className="flex items-center justify-center pr-0"
                    key={url + index}
                    data-active={current === index + 1}
                    data-testid={`file-preview-item-image-previewed-url`}
                  >
                    <ZoomableImage src={url} alt={url} />
                  </CarouselItem>
                );
              }
            })}
        </CarouselContent>
        <div className="absolute left-0.5 top-1/2 -translate-y-1/2">
          <CarouselPrevious data-testid="file-preview-item-carousel-previous" />
        </div>
        <div className="absolute right-0.5 top-1/2 -translate-y-1/2">
          <CarouselNext data-testid="file-preview-item-carousel-next" />
        </div>
      </Carousel>
    </div>,
    document.body
  );
};
